// src/app/core/shared/t0-model/analysis.store.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { T0ApiResponse, Year } from '../t0-model/t0-response-model';

export type MetricValue =
  | 'controlled'
  | 'treated_not_controlled'
  | 'diagnosed'
  | 'undiagnosed'
  | 'prevalence'
  | 'screened'
  | 'aware_of_screening'
  | 'cvd_events'
  | 'cvd_patients'
  | 'cvd_deaths';

export type GroupValue = 'total' | 'male' | 'female' | 'average';

export type SeriesPoint = { x: number; y: number };
export type Series = { label: string; points: SeriesPoint[] };

export interface AnalysisRun {
  id: string;
  title: string;
  createdAt: number;
  t0: T0ApiResponse;
  isBaseline?: boolean;
}

const LS_KEY = 't0_runs_v1';
const SEL_KEY = 't0_selected_run_id_v1';
const CITY_KEY = 't0_runs_city_v1';

@Injectable({ providedIn: 'root' })
export class AnalysisStore {
  private runsListSubject = new BehaviorSubject<AnalysisRun[]>([]);
  runsList$ = this.runsListSubject.asObservable();

  private selectedRunIdSubject = new BehaviorSubject<string | null>(null);
  selectedRunId$ = this.selectedRunIdSubject.asObservable();

  metric$ = new BehaviorSubject<MetricValue>('controlled');
  group$ = new BehaviorSubject<GroupValue>('total');

  combinedSeries$ = new BehaviorSubject<Series[]>([]);

  constructor() {
    const runs = this.loadRunsFromStorage();
    if (runs.length) this.runsListSubject.next(runs);

    const sel = this.loadSelectedRunId();
    if (sel) this.selectedRunIdSubject.next(sel);
    else if (runs.length) {
      const baseline = runs.find((r) => (r.title ?? '').trim().toLowerCase() === 'basic simulation');
      this.selectedRunIdSubject.next((baseline ?? runs[runs.length - 1]).id);
    }

    this.selectedRunIdSubject.subscribe((id) => {
      try {
        if (id) localStorage.setItem(SEL_KEY, id);
        else localStorage.removeItem(SEL_KEY);
      } catch { }
    });

    this.rebuildCombinedSeries();
  }

  // ---- Selection helpers ----
  setSelectedRunId(id: string | null): void {
    this.selectedRunIdSubject.next(id);
  }

  getSelectedRunId(): string | null {
    return this.selectedRunIdSubject.value;
  }

  // ---- Snapshot helpers (used by components like ProjectedImpact) ----
  getRunsSnapshot(): AnalysisRun[] {
    return this.runsListSubject.value ?? [];
  }

  getRunByTitle(title: string): AnalysisRun | null {
    const t = (title || '').trim().toLowerCase();
    const runs = this.getRunsSnapshot();
    return runs.find((r) => (r.title || '').trim().toLowerCase() === t) ?? null;
  }

  getBaselineOrFirst(): AnalysisRun | null {
    const runs = this.getRunsSnapshot();
    if (!runs.length) return null;
    return runs.find((r) => (r.title || '').trim().toLowerCase() === 'basic simulation') ?? runs[0];
  }

  // ---- Public API ----
  setRuns(runs: AnalysisRun[]) {
    this.runsListSubject.next(runs);
    this.persistRuns(runs);
    this.rebuildCombinedSeries();
  }

  /**
   * Adds a new run and selects it.
   * Prevents duplicates by title (case-insensitive) by replacing existing instead of adding.
   */
  upsertRun(t0: T0ApiResponse, title: string, createdAt = Date.now()) {
    const normalizedTitle = (title || '').trim();
    if (!normalizedTitle) return;

    const prev = this.getRunsSnapshot();
    const existingIdx = prev.findIndex((r) => (r.title || '').trim().toLowerCase() === normalizedTitle.toLowerCase());

    const baselineTitle = 'Basic Simulation';
    const isBaseline = normalizedTitle.toLowerCase() === 'baseline' || normalizedTitle.toLowerCase() === baselineTitle.toLowerCase();

    if (existingIdx >= 0) {
      const next = [...prev];
      const existing = next[existingIdx];
      next[existingIdx] = { ...existing, title: isBaseline ? baselineTitle : normalizedTitle, t0, createdAt };
      this.runsListSubject.next(next);
      this.persistRuns(next);
      this.rebuildCombinedSeries();
      this.selectedRunIdSubject.next(existing.id);
      return;
    }

    const nextIdNum = this.nextId(prev);
    const run: AnalysisRun = {
      id: String(nextIdNum),
      title: isBaseline ? baselineTitle : normalizedTitle,
      createdAt,
      t0,
      isBaseline
    };

    const next = [...prev, run];
    this.runsListSubject.next(next);
    this.persistRuns(next);
    this.rebuildCombinedSeries();
    this.selectedRunIdSubject.next(run.id);
  }

  appendRun(t0: T0ApiResponse, title?: string) {
    const t = (title || '').trim();
    this.upsertRun(t0, t || (this.nextId(this.getRunsSnapshot()) === 1 ? 'Basic Simulation' : `Simulation ${this.nextId(this.getRunsSnapshot())}`));
  }

  renameRunTitle(oldTitle: string, newTitle: string) {
    const o = (oldTitle || '').trim().toLowerCase();
    const n = (newTitle || '').trim();
    if (!o || !n) return;

    // prevent duplicates
    const exists = this.getRunsSnapshot().some((r) => (r.title || '').trim().toLowerCase() === n.toLowerCase());
    if (exists) return;

    const prev = this.getRunsSnapshot();
    const next = prev.map((r) => ((r.title || '').trim().toLowerCase() === o ? { ...r, title: n } : r));
    this.runsListSubject.next(next);
    this.persistRuns(next);
    this.rebuildCombinedSeries();
  }

  deleteRunsByTitle(titles: string[]) {
    const toDelete = new Set((titles || []).map((t) => (t || '').trim().toLowerCase()));
    const prev = this.getRunsSnapshot();

    const next = prev.filter((r) => !toDelete.has((r.title || '').trim().toLowerCase()));
    this.runsListSubject.next(next);
    this.persistRuns(next);
    this.rebuildCombinedSeries();

    const currentSel = this.selectedRunIdSubject.value;
    if (currentSel && !next.some((r) => r.id === currentSel)) {
      const baseline = next.find((r) => (r.title ?? '').trim().toLowerCase() === 'basic simulation');
      this.selectedRunIdSubject.next((baseline ?? next[next.length - 1] ?? null)?.id ?? null);
    }
  }

  loadRunsFromStorage(): AnalysisRun[] {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as AnalysisRun[]) : [];
    } catch {
      return [];
    }
  }

  getBaselineRun(): AnalysisRun | null {
    const runs = this.getRunsSnapshot();
    return runs.find((r) => (r.title ?? '').trim().toLowerCase() === 'basic simulation') ?? null;
  }

  // ---- Helpers for metrics/series ----
  getLatestYear(t0: T0ApiResponse): Year | null {
    const ys = Array.isArray(t0?.years) ? t0.years : [];
    return ys.length ? ys[ys.length - 1] : null;
  }

  pickNearestOrLatestYear(t0: T0ApiResponse, desiredYear: number): Year | null {
    const years = Array.isArray(t0?.years) ? t0.years.map(Number).filter(Number.isFinite) : [];
    if (!years.length) return null;
    if (years.includes(desiredYear)) return desiredYear as Year;
    return years[years.length - 1] as Year; // fallback to latest available
  }

  clamp0(n: any): number {
    const v = Number(n);
    return Number.isFinite(v) ? Math.max(0, v) : 0;
  }

  pick(t0: T0ApiResponse, key: string, year: Year | null): number {
    if (!t0 || year === null) return 0;
    const series = (t0 as any)[key] as Record<number, number> | undefined;
    const raw = series ? (series as any)[year] : 0;
    return this.clamp0(raw);
  }

  pickGroup(t0: T0ApiResponse, baseKey: string, year: Year | null, group: GroupValue): number {
    if (year === null) return 0;

    const totalKey = baseKey;
    const maleKey = `${baseKey}_Male`;
    const femaleKey = `${baseKey}_Female`;

    const total = this.pick(t0, totalKey, year);
    const male = this.pick(t0, maleKey, year);
    const female = this.pick(t0, femaleKey, year);

    if (group === 'male') return male;
    if (group === 'female') return female;
    if (group === 'total') return total;
    return (male + female) / 2;
  }

  metricValueAt(t0: T0ApiResponse, metric: MetricValue, group: GroupValue, year: Year | null): number {
    const g = (base: string) => this.pickGroup(t0, base, year, group);

    switch (metric) {
      case 'undiagnosed':
        return g('Undiagnosed');
      case 'diagnosed':
        return g('Diagnosed');
      case 'controlled':
        return g('Controlled');

      case 'treated_not_controlled': {
        const treated = g('Treated');
        const controlled = g('Controlled');
        return Math.max(0, treated - controlled);
      }

      case 'prevalence': {
        const d = g('Diagnosed');
        const u = g('Undiagnosed');
        return Math.max(0, d + u);
      }

      case 'screened': {
        const male = this.pick(t0, 'Screened_Male', year);
        const female = this.pick(t0, 'Screened_Female', year);
        if (group === 'male') return male;
        if (group === 'female') return female;
        if (group === 'total') return male + female;
        return (male + female) / 2;
      }

      case 'aware_of_screening': {
        const male = this.pick(t0, 'Aware_of_Screening_Male', year);
        const female = this.pick(t0, 'Aware_of_Screening_Female', year);
        if (group === 'male') return male;
        if (group === 'female') return female;
        if (group === 'total') return male + female;
        return (male + female) / 2;
      }

      case 'cvd_events':
        return g('CVD_Events');
      case 'cvd_patients':
        return g('CVD_Patients');
      case 'cvd_deaths':
        return g('CVD_Deaths');
    }
  }

  seriesForRun(run: AnalysisRun, metric: MetricValue, group: GroupValue): Series {
    const t0 = run.t0;
    const years = Array.isArray(t0?.years) ? t0.years : [];
    const points = years.map((y) => ({
      x: Number(y),
      y: Math.round(this.metricValueAt(t0, metric, group, y))
    }));
    return { label: run.title, points };
  }

  rebuildCombinedSeries() {
    const runs = this.getRunsSnapshot();
    const metric = this.metric$.value;
    const group = this.group$.value;
    const seriesList = runs.map((r) => this.seriesForRun(r, metric, group));
    this.combinedSeries$.next(seriesList);
  }

  onMetricOrGroupChange() {
    this.rebuildCombinedSeries();
  }

  // ---- Persistence ----
  private persistRuns(runs: AnalysisRun[]) {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(runs));
    } catch { }
  }

  private loadSelectedRunId(): string | null {
    try {
      return localStorage.getItem(SEL_KEY);
    } catch {
      return null;
    }
  }

  private nextId(prev: AnalysisRun[]): number {
    const nums = prev.map((r) => Number(r.id)).filter((n) => Number.isFinite(n));
    const max = nums.length ? Math.max(...nums) : 0;
    return max + 1;
  }

  getPersistedCity(): string | null {
    try {
      return localStorage.getItem(CITY_KEY);
    } catch {
      return null;
    }
  }

  setPersistedCity(city: string | null): void {
    try {
      if (city && city.trim()) {
        localStorage.setItem(CITY_KEY, city.trim());
      } else {
        localStorage.removeItem(CITY_KEY);
      }
    } catch {}
  }

  hasRunsForCity(city: string): boolean {
    const current = (this.getPersistedCity() || '').trim().toLowerCase();
    const incoming = (city || '').trim().toLowerCase();
    return !!incoming && current === incoming && this.getRunsSnapshot().length > 0;
  }

  clearAllRuns(): void {
    this.runsListSubject.next([]);
    this.selectedRunIdSubject.next(null);
    try {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(SEL_KEY);
      localStorage.removeItem(CITY_KEY);
    } catch {}
    this.rebuildCombinedSeries();
  }

  setBaselineRun(t0: T0ApiResponse, city: string, createdAt = Date.now()): void {
    const baseline: AnalysisRun = {
      id: '1',
      title: 'Basic Simulation',
      createdAt,
      t0,
      isBaseline: true
    };

    this.runsListSubject.next([baseline]);
    this.selectedRunIdSubject.next(baseline.id);
    this.persistRuns([baseline]);
    this.setPersistedCity(city);
    this.rebuildCombinedSeries();
  }
}